import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { money, startOfWeek, addDays, toISODate, dayLabel, dayNum, fmtDate } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { DatePicker } from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Clock, Plus, ChevronLeft, ChevronRight, Trash2, Play, Square, Download } from "lucide-react";
import { toast } from "sonner";
import { useTimer } from "@/context/TimerContext";
import { exportCsv } from "@/lib/csv";

export default function Timesheets() {
  const [jobs, setJobs] = useState([]);
  const [entries, setEntries] = useState([]);
  const [grid, setGrid] = useState(null);
  const [weekStart, setWeekStart] = useState(startOfWeek());
  const [loading, setLoading] = useState(true);
  const { active, fmtElapsed, clockIn, clockOut } = useTimer();
  const [timerJob, setTimerJob] = useState("");

  const [form, setForm] = useState({ job_id: "", work_date: toISODate(new Date()), hours: "", notes: "", is_billable: true });

  const loadJobs = useCallback(async () => {
    const j = (await api.get("/jobs")).data;
    setJobs(j);
    if (j.length && !form.job_id) setForm((f) => ({ ...f, job_id: j[0].id }));
    if (j.length && !timerJob) setTimerJob(j[0].id);
  }, []); // eslint-disable-line

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const ws = toISODate(weekStart);
      const [g, e] = await Promise.all([
        api.get(`/timesheets/weekly?week_start=${ws}`),
        api.get(`/timesheets`),
      ]);
      setGrid(g.data);
      setEntries(e.data);
    } finally { setLoading(false); }
  }, [weekStart]);

  useEffect(() => { loadJobs(); }, [loadJobs]);
  useEffect(() => { loadData(); }, [loadData]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.job_id) return toast.error("Select a job");
    const hours = parseFloat(form.hours);
    if (!hours || hours <= 0) return toast.error("Enter valid hours");
    try {
      await api.post("/timesheets", { ...form, hours });
      toast.success("Time logged");
      setForm((f) => ({ ...f, hours: "", notes: "" }));
      loadData();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save"); }
  };

  const del = async (id) => {
    try { await api.delete(`/timesheets/${id}`); toast.success("Entry removed"); loadData(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not delete"); }
  };

  const days = grid?.days || [];
  const weekLabel = `${fmtDate(toISODate(weekStart))} – ${fmtDate(toISODate(addDays(weekStart, 6)))}`;

  const exportEntries = () => {
    if (entries.length === 0) return toast.error("No entries to export");
    exportCsv("timesheets.csv", [
      { label: "Date", accessor: "work_date" },
      { label: "Job", accessor: "job_title" },
      { label: "Hours", accessor: (r) => r.hours.toFixed(2) },
      { label: "Rate", accessor: (r) => r.hourly_rate },
      { label: "Amount", accessor: (r) => (r.hours * (r.hourly_rate || 0)).toFixed(2) },
      { label: "Billable", accessor: (r) => (r.is_billable ? "Yes" : "No") },
      { label: "Invoiced", accessor: (r) => (r.invoice_id ? "Yes" : "No") },
      { label: "Notes", accessor: "notes" },
    ], entries);
    toast.success("Timesheets exported");
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Timesheets" subtitle="Log hours fast, or track live with the timer."
        actions={<Button variant="outline" data-testid="timesheets-export-button" onClick={exportEntries}><Download className="h-4 w-4 mr-2" />Export CSV</Button>} />

      {/* Live timer control */}
      <Card className="p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2 text-sm font-medium"><Clock className="h-4 w-4 text-primary" />Live timer</div>
          {active ? (
            <div className="flex items-center gap-3 flex-wrap">
              <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
              <span className="text-sm text-muted-foreground">{active.job_title}</span>
              <span className="font-mono text-lg font-semibold tabular-nums">{fmtElapsed()}</span>
              <Button variant="destructive" size="sm" data-testid="timesheet-timer-stop" onClick={async () => { await clockOut(); loadData(); }}><Square className="h-3.5 w-3.5 mr-1" />Clock out</Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={timerJob} onValueChange={setTimerJob}>
                <SelectTrigger className="w-56" data-testid="timesheet-timer-job-select"><SelectValue placeholder="Select job" /></SelectTrigger>
                <SelectContent>{jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.title}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="sm" data-testid="timesheet-timer-start" disabled={!timerJob} onClick={() => clockIn(timerJob)}><Play className="h-3.5 w-3.5 mr-1" />Clock in</Button>
            </div>
          )}
        </div>
      </Card>

      {/* Rapid entry */}
      <Card className="p-4 sm:p-5">
        <h3 className="text-sm font-semibold mb-3">Quick log</h3>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end">
          <div className="sm:col-span-3 space-y-1.5"><Label>Date</Label><DatePicker value={form.work_date} onChange={(v) => setForm({ ...form, work_date: v })} testid="timesheet-entry-date-input" /></div>
          <div className="sm:col-span-3 space-y-1.5"><Label>Job</Label>
            <Select value={form.job_id} onValueChange={(v) => setForm({ ...form, job_id: v })}>
              <SelectTrigger data-testid="timesheet-entry-job-select"><SelectValue placeholder="Select job" /></SelectTrigger>
              <SelectContent>{jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.title}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2 space-y-1.5"><Label>Hours</Label><Input type="number" step="0.25" min="0" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} data-testid="timesheet-entry-hours-input" placeholder="2.5" /></div>
          <div className="sm:col-span-2 space-y-1.5"><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="timesheet-entry-notes-input" placeholder="What did you do?" /></div>
          <div className="sm:col-span-2 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2"><Switch checked={form.is_billable} onCheckedChange={(v) => setForm({ ...form, is_billable: v })} data-testid="timesheet-entry-billable-switch" /><span className="text-xs text-muted-foreground">Billable</span></div>
            <Button type="submit" data-testid="timesheet-save-entry-button"><Plus className="h-4 w-4 mr-1" />Log</Button>
          </div>
        </form>
      </Card>

      {/* Weekly grid */}
      <Card className="overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold font-display">Weekly grid</h3>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" className="h-8 w-8" data-testid="timesheets-week-prev" onClick={() => setWeekStart(addDays(weekStart, -7))}><ChevronLeft className="h-4 w-4" /></Button>
            <span className="text-sm font-medium min-w-[180px] text-center" data-testid="timesheets-week-label">{weekLabel}</span>
            <Button variant="outline" size="icon" className="h-8 w-8" data-testid="timesheets-week-next" onClick={() => setWeekStart(addDays(weekStart, 7))}><ChevronRight className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" data-testid="timesheets-week-today" onClick={() => setWeekStart(startOfWeek())}>Today</Button>
          </div>
        </div>
        {loading ? <div className="p-8 text-center text-muted-foreground">Loading...</div>
        : !grid || grid.rows.length === 0 ? (
          <div className="p-8"><EmptyState icon={Clock} title="No hours this week" description="Use the quick log or the timer above to record your first entry." /></div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="timesheets-weekly-grid">
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[160px]">Job</TableHead>
                  {days.map((d) => <TableHead key={d} className="text-center">{dayLabel(d)}<div className="text-[11px] text-muted-foreground font-normal">{dayNum(d)}</div></TableHead>)}
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {grid.rows.map((r) => (
                  <TableRow key={r.job_id}>
                    <TableCell className="font-medium">{r.job_title}</TableCell>
                    {days.map((d) => <TableCell key={d} className="text-center tabular-nums text-sm">{r.days[d] ? r.days[d].toFixed(2) : <span className="text-muted-foreground/40">·</span>}</TableCell>)}
                    <TableCell className="text-right tabular-nums font-semibold">{r.total.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-secondary/50 font-medium">
                  <TableCell>Daily total</TableCell>
                  {days.map((d) => <TableCell key={d} className="text-center tabular-nums">{grid.day_totals[d] ? grid.day_totals[d].toFixed(2) : "0"}</TableCell>)}
                  <TableCell className="text-right tabular-nums" data-testid="timesheets-week-total">{grid.week_total.toFixed(2)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Recent entries */}
      <Card className="overflow-hidden">
        <div className="p-4 border-b"><h3 className="text-lg font-semibold font-display">Recent entries</h3></div>
        {entries.length === 0 ? <div className="p-8 text-center text-muted-foreground text-sm">No entries yet.</div> : (
          <Table data-testid="timesheets-entries-table">
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Job</TableHead><TableHead className="hidden sm:table-cell">Notes</TableHead><TableHead className="text-right">Hours</TableHead><TableHead>Status</TableHead><TableHead className="text-right"></TableHead></TableRow></TableHeader>
            <TableBody>
              {entries.slice(0, 40).map((e) => (
                <TableRow key={e.id} data-testid={`timesheet-entry-row-${e.id}`} className="hover:bg-accent/50">
                  <TableCell className="whitespace-nowrap">{fmtDate(e.work_date)}</TableCell>
                  <TableCell className="font-medium">{e.job_title}</TableCell>
                  <TableCell className="hidden sm:table-cell text-sm text-muted-foreground max-w-[220px] truncate">{e.notes || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{e.hours.toFixed(2)}</TableCell>
                  <TableCell>
                    {e.invoice_id ? <Badge variant="outline">Invoiced</Badge> : e.is_billable ? <Badge className="bg-[color:var(--kpi-positive)] text-white">Billable</Badge> : <Badge variant="secondary">Non-billable</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    {!e.invoice_id && <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" data-testid={`timesheet-delete-${e.id}`} onClick={() => del(e.id)}><Trash2 className="h-4 w-4" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
